const ASI_MEDIA_BASE_URL = "https://api.asicentral.com/v1/media/";
const ASI_API_BASE_URL = "https://api.asicentral.com/v1/";
const ASI_MEDIA_URL_PATTERN = /^https:\/\/api\.asicentral\.com\/v1\/media\/(\d+)(?:[/?#].*)?$/i;
const ASI_MEDIA_PATH_PATTERN = /^\/?(?:v1\/)?media\/(\d+)(?:[/?#].*)?$/i;
const QUERY_PARAM_PATTERN = /([?&])size=[^&#]*/i;

const normalizeMediaId = (value = "") => String(value || "").trim();

export const isAbsoluteUrl = (value = "") => /^https?:\/\//i.test(String(value || "").trim());

export const getAsiMediaId = (value = "") => {
  const raw = String(value || "").trim();
  if (!raw) return "";

  const absoluteMatch = raw.match(ASI_MEDIA_URL_PATTERN);
  if (absoluteMatch?.[1]) return normalizeMediaId(absoluteMatch[1]);

  const pathMatch = raw.match(ASI_MEDIA_PATH_PATTERN);
  if (pathMatch?.[1]) return normalizeMediaId(pathMatch[1]);

  if (/^\d+$/.test(raw)) return normalizeMediaId(raw);

  return "";
};

export const toAsiNormalImageUrl = (value = "") => {
  const raw = String(value || "").trim();
  if (!raw) return "";

  const mediaId = getAsiMediaId(raw);
  if (mediaId) {
    return `${ASI_MEDIA_BASE_URL}${mediaId}?size=normal`;
  }

  if (isAbsoluteUrl(raw)) {
    if (QUERY_PARAM_PATTERN.test(raw)) {
      return raw.replace(QUERY_PARAM_PATTERN, "$1size=normal");
    }
    return raw;
  }

  const cleaned = raw.replace(/^\/+/, "").replace(/^v1\//i, "");
  if (!cleaned) return "";

  if (/^media\/\d+(?:$|[?#])/i.test(cleaned)) {
    const cleanedMediaId = getAsiMediaId(cleaned);
    return cleanedMediaId ? `${ASI_MEDIA_BASE_URL}${cleanedMediaId}?size=normal` : "";
  }

  return `${ASI_API_BASE_URL}${cleaned}`;
};

export const canonicalizeImageUrl = (value = "") => {
  const raw = String(value || "").trim();
  if (!raw) return "";

  const mediaId = getAsiMediaId(raw);
  if (mediaId) return `asi-media:${mediaId}`;

  if (isAbsoluteUrl(raw)) {
    try {
      const url = new URL(raw);
      url.hash = "";
      if (url.hostname.toLowerCase() === "api.asicentral.com" && url.pathname.toLowerCase().startsWith("/v1/media/")) {
        const normalizedMediaId = getAsiMediaId(url.toString());
        if (normalizedMediaId) return `asi-media:${normalizedMediaId}`;
      }
      return url.toString();
    } catch {
      return raw;
    }
  }

  return raw.replace(/^\/+/, "").replace(/^v1\//i, "").toLowerCase();
};

const normalizeAsiImageCandidate = (value) => {
  if (!value) return "";
  if (typeof value === "string") return toAsiNormalImageUrl(value);

  const direct =
    value.ImageUrl ||
    value.Url ||
    value.Value ||
    value.MediaUrl ||
    value.OriginalUrl ||
    value.NormalUrl ||
    value.ThumbnailUrl ||
    value.LargeUrl;

  if (direct) return toAsiNormalImageUrl(direct);

  const mediaId = value.Id || value.MediaId || value.MediaID || value.id;
  return mediaId ? toAsiNormalImageUrl(String(mediaId)) : "";
};

export const collectEditItemImageUrls = (orderItem, asiLiveRawData) => {
  const fromOrder = Array.isArray(orderItem?.ImageUrls)
    ? orderItem.ImageUrls
    : Array.isArray(orderItem?.imageUrls)
      ? orderItem.imageUrls
      : [];

  const fromOrderSingle = [
    orderItem?.ImageUrl,
    orderItem?.imageUrl,
    orderItem?.Image,
    orderItem?.image,
  ].map(normalizeAsiImageCandidate);

  const asiImagesRaw = Array.isArray(asiLiveRawData?.Images?.Values)
    ? asiLiveRawData.Images.Values
    : Array.isArray(asiLiveRawData?.Images)
      ? asiLiveRawData.Images
      : [];

  const fromAsiImages = asiImagesRaw.map(normalizeAsiImageCandidate);

  const fromAsiVariants = Array.isArray(asiLiveRawData?.Variants)
    ? asiLiveRawData.Variants.flatMap((variant) => ([
      variant?.ImageUrl,
      variant?.Image?.ImageUrl,
      variant?.Image?.Url,
      variant?.MediaId,
    ].map(normalizeAsiImageCandidate)))
    : [];

  const fromSingle = [
    asiLiveRawData?.ImageUrl,
    asiLiveRawData?.PrimaryImageUrl,
    asiLiveRawData?.MainImageUrl,
  ].map(normalizeAsiImageCandidate);

  const deduped = [];
  const seen = new Set();

  [...fromOrder, ...fromOrderSingle, ...fromAsiImages, ...fromAsiVariants, ...fromSingle].forEach((candidate) => {
    const normalized = normalizeAsiImageCandidate(candidate);
    const key = canonicalizeImageUrl(normalized);
    if (!normalized || !key || seen.has(key)) return;
    seen.add(key);
    deduped.push(normalized);
  });

  return deduped;
};

export const findImageIndex = (images = [], selectedImage = "") => {
  const selectedKey = canonicalizeImageUrl(selectedImage);
  if (!selectedKey) return 0;
  const index = images.findIndex((image) => canonicalizeImageUrl(image) === selectedKey);
  return index >= 0 ? index : 0;
};

const normalizePromoStoreGalleryCandidate = (value = "") => {
  const normalized = normalizeAsiImageCandidate(value);
  return String(normalized || "").trim();
};

export const normalizePromoStoreImageSelection = ({
  selectedImage = "",
  imageUrls = [],
} = {}) => {
  const ordered = [];
  const seen = new Set();

  [selectedImage, ...(Array.isArray(imageUrls) ? imageUrls : [])].forEach((value) => {
    const normalized = normalizePromoStoreGalleryCandidate(value);
    const key = canonicalizeImageUrl(normalized);
    if (!normalized || !key || seen.has(key)) return;
    seen.add(key);
    ordered.push(normalized);
  });

  return {
    selectedImage: ordered[0] || "",
    imageUrls: ordered,
  };
};

export const togglePromoStoreImageSelection = (imageUrls = [], value = "") => {
  const normalizedValue = normalizePromoStoreGalleryCandidate(value);
  const current = normalizePromoStoreImageSelection({ imageUrls }).imageUrls;
  const keyToToggle = canonicalizeImageUrl(normalizedValue);

  if (!normalizedValue || !keyToToggle) {
    return current;
  }

  const next = current.filter((image) => canonicalizeImageUrl(image) !== keyToToggle);
  if (next.length !== current.length) {
    return next;
  }

  return [...current, normalizedValue];
};

export const appendPromoStoreImageSelection = (imageUrls = [], value = "") =>
  normalizePromoStoreImageSelection({
    imageUrls: [...(Array.isArray(imageUrls) ? imageUrls : []), value],
  }).imageUrls;
